import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { AppError } from '@interticket/core';
import { NotifierService } from '../notification/notifier.service';
import { ErrorLoggerService } from './error-logger.service';

const CHUNK_LOAD_FAILURE_PATTERN =
  /Loading chunk .+ failed|ChunkLoadError|(Failed to fetch|error loading) dynamically imported module|Importing a module script failed/i;
const CHUNK_RELOAD_STORAGE_KEY = 'chunk-load-reloaded-at';
const CHUNK_RELOAD_COOLDOWN_MS = 30000;

/**
 * {@link} https://medium.com/angular-in-depth/expecting-the-unexpected-best-practices-for-error-handling-in-angular-21c3662ef9e4
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {

  constructor(
    private injector: Injector,
  ) { }

  handleError(error: AppError): void {
    // Angular Lazy Routes & loading chunk failed
    // https://medium.com/@kamrankhatti/angular-lazy-routes-loading-chunk-failed-42b16c22a377
    if (this.isChunkLoadError(error) && this.reloadOnce()) {
      return;
    }

    const logger = this.injector.get(ErrorLoggerService);
    const notifier = this.injector.get(NotifierService);

    // Show notification
    notifier.showError(error);

    // Logging Error
    logger.logError(error);
  }

  private isChunkLoadError(error: AppError): boolean {
    return CHUNK_LOAD_FAILURE_PATTERN.test(error?.name ?? '') || CHUNK_LOAD_FAILURE_PATTERN.test(error?.message ?? '');
  }

  // A chunk that fails because the gateway rate-limited it would reload into the same wall,
  // so recover once and let the next failure surface as a normal error.
  private reloadOnce(): boolean {
    const now: number = Date.now();

    if (now - this.getLastReloadAt() < CHUNK_RELOAD_COOLDOWN_MS) {
      return false;
    }

    this.setLastReloadAt(now);
    window.location.reload();

    return true;
  }

  private getLastReloadAt(): number {
    try {
      return Number(window.sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY)) || 0;
    } catch {
      return 0;
    }
  }

  private setLastReloadAt(at: number): void {
    try {
      window.sessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(at));
    } catch {
      return;
    }
  }

}
